import { FC, ReactNode } from 'react'
import {
  InboxAnalysisPage,
  InboxAnalysisPageKeyInsights,
  InboxAnalysisPageOperator,
} from 'src/reactQuery/useGetInboxAnalysisPagesQuery'
import classes from './pageCard.module.scss'

const getInitial = (name: string) => name.trim().charAt(0).toUpperCase() || '?'

export const formatResponseMinutes = (minutes: number) => {
  if (minutes < 1) return '<1 min'
  if (minutes < 60) return `${Math.round(minutes)} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const rest = Math.round(minutes % 60)
    return rest ? `${hours}h ${rest}m` : `${hours}h`
  }
  const days = Math.floor(hours / 24)
  const restHours = hours % 24
  return restHours ? `${days}d ${restHours}h` : `${days}d`
}

export const TopOperatorsList: FC<{
  operators: InboxAnalysisPageOperator[]
}> = ({ operators }) => (
  <section className={classes.footerSection}>
    <h3 className={classes.footerTitle}>Top Operators</h3>
    <ol className={classes.operatorList}>
      {operators.map((operator, index) => (
        <li className={classes.operatorRow} key={operator.id}>
          <span className={classes.operatorRank}>{index + 1}</span>
          <span aria-hidden="true" className={classes.operatorAvatar}>
            {getInitial(operator.name)}
          </span>
          <span className={classes.operatorName} title={operator.name}>
            {operator.name}
          </span>
          {typeof operator.chats === 'number' && (
            <span className={classes.operatorCount}>
              <strong>{operator.chats.toLocaleString()}</strong> chats
            </span>
          )}
        </li>
      ))}
    </ol>
  </section>
)

const iconProps = {
  'aria-hidden': true,
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  strokeWidth: 1.6,
  viewBox: '0 0 24 24',
}

const ChatIcon = () => (
  <svg {...iconProps}>
    <path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4A8 8 0 1 1 20 12Z" />
  </svg>
)
const TagIcon = () => (
  <svg {...iconProps}>
    <path d="M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9-9-9Z" />
    <circle cx="7.5" cy="7.5" r="1.2" />
  </svg>
)
const CalendarIcon = () => (
  <svg {...iconProps}>
    <rect height="16" rx="2" width="18" x="3" y="5" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
)
const ClockIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
)

const INSIGHTS: {
  format?: (value: number) => string
  icon: ReactNode
  key: keyof InboxAnalysisPageKeyInsights
  label: string
}[] = [
  { icon: <ChatIcon />, key: 'topTopic', label: 'Top Topic' },
  { icon: <CalendarIcon />, key: 'peakDay', label: 'Peak Day' },
  { icon: <TagIcon />, key: 'topCategory', label: 'Top Category' },
  {
    format: formatResponseMinutes,
    icon: <ClockIcon />,
    key: 'avgResponseMinutes',
    label: 'Avg. Response',
  },
]

export const KeyInsightsGrid: FC<{
  insights: InboxAnalysisPageKeyInsights
}> = ({ insights }) => (
  <section className={classes.footerSection}>
    <h3 className={classes.footerTitle}>Key Insights</h3>
    <dl className={classes.insightsGrid}>
      {INSIGHTS.map(({ format, icon, key, label }) => {
        const raw = insights[key]
        const value =
          raw === null || raw === undefined
            ? '—'
            : typeof raw === 'number'
              ? format?.(raw) ?? raw.toLocaleString()
              : raw
        return (
          <div className={classes.insight} key={key}>
            <span className={classes.insightIcon}>{icon}</span>
            <div className={classes.insightText}>
              <dt className={classes.insightLabel}>{label}</dt>
              <dd className={classes.insightValue} title={String(value)}>
                {value}
              </dd>
            </div>
          </div>
        )
      })}
    </dl>
  </section>
)

export const PageCardFooter: FC<{ page: InboxAnalysisPage }> = ({ page }) => {
  const showOperators =
    page.topOperators.length > 0 &&
    (page.hasOperatorAttribution !== false || !page.keyInsights)

  if (showOperators) return <TopOperatorsList operators={page.topOperators} />
  if (page.keyInsights) return <KeyInsightsGrid insights={page.keyInsights} />
  return null
}
